<template>
  <PageSection>
    <PageSectionHeading>
      {{ formTitle }}
    </PageSectionHeading>
    <form
      class="grid grid-cols-1 xl:grid-cols-2 gap-6 relative"
      @submit.prevent="submit()"
    >
      <template
        v-if="!$store.state.auth.contact.exists()"
      >
        <div>
          <TextInput
            v-model:value="contact.first_name"
            :error-messages="errors.first_name || []"
            label="First Name"
            name="first_name"
          />
        </div>
        <div>
          <TextInput
            v-model:value="contact.last_name"
            :error-messages="errors.last_name || []"
            label="Last Name"
            name="last_name"
          />
        </div>
        <div>
          <TextInput
            v-model:value="maskedFields.phoneNumber"
            :error-messages="errors.phone_number || []"
            label="Phone Number"
            mask="(###) ###-####"
            name="phone_number"
            @update:value="updatePhoneNumber($event)"
          />
        </div>
        <div>
          <TextInput
            v-model:value="contact.email"
            :error-messages="errors.email || []"
            label="Email"
            name="email"
            type="email"
          />
        </div>
      </template>
      <div
        class="xl:col-span-2"
      >
        <TextareaInput
          v-model:value="message.body"
          :disabled="message.exists()"
          :error-messages="errors.message || []"
          label="Message"
          name="message"
        />
      </div>
      <div
        class="xl:col-span-2"
      >
        <Btn
          :disabled="message.exists()"
          :label="message.exists() ? 'Message Sent!' : 'Send'"
          :loading="isLoading"
          :success="message.exists()"
        />
      </div>
      <Alert>
        {{ feedback }}
      </Alert>
      <Loader
        v-show="isLoading"
      />
    </form>
  </PageSection>
</template>

<script>
import { Contact, Message } from 'displet-sdk-js';
import { isEmptyString } from 'mytribus-commons';

import { mapGetters, mapActions } from 'vuex';
import Alert from '../components/Alert.vue';
import Btn from '../components/Btn.vue';
import generatePassword from '../../../helpers/generatePassword';
import Loader from '../components/Loader.vue';
import PageSection from '../components/PageSection.vue';
import PageSectionHeading from '../components/PageSectionHeading.vue';
import TextareaInput from '../components/TextareaInput.vue';
import TextInput from '../components/TextInput.vue';
import getBrytescoreListing from '../../../helpers/brytescore';

export default {
  name: 'PropertyRequestInfo',

  display({ isBranded }) {
    return isBranded;
  },

  components: {
    Alert,
    Btn,
    Loader,
    PageSection,
    PageSectionHeading,
    TextInput,
    TextareaInput,
  },

  props: {
    listAgents: {
      required: true,
      type: Array,
    },
    myTribusEmbed: {
      required: true,
      type: Object,
    },
    property: {
      required: true,
      type: Object,
    },
  },

  setup() {
    return {
      formTitle: 'Request More Info',
    };
  },

  data() {
    return {
      contact: new Contact(),
      errors: {},
      feedback: null,
      isLoading: false,
      maskedFields: {
        phoneNumber: null,
      },
      message: new Message(),
    };
  },

  computed: {
    ...mapGetters('auth', [
      'contactTelephone',
      'contactName',
      'contactEmail',
      'agentName',
      'agentEmail',
    ]),
  },

  methods: {
    ...mapActions('auth', [
      'fetchAgent',
    ]),
    /** @return {Promise<void>} */
    async createContactAndLogin() {
      this.contact.display_name = this.contact.getFullName();
      this.contact.password = generatePassword();
      this.contact.referred_by = window.location.href;
      this.contact.user_id = this.listAgents.length > 0
        ? this.listAgents[0].id
        : null;

      if (
        this.contact.hasPrimary('phone_number')
        && this.contact.getPrimary('phone_number').number.length !== 10
      ) {
        this.clearErrors();
        this.errors.phone_number = [
          'The phone number must be valid.',
        ];

        throw new Error('Invalid phone number.');
      }

      try {
        await this.contact.save();
        this.clearErrors();
      } catch (e) {
        this.clearErrors();

        Object.keys(e).forEach((key) => {
          const fieldNameMap = new Map();
          fieldNameMap.set('login', 'email');
          fieldNameMap.set('phone_numbers.number', 'phone_number');

          const fieldName = fieldNameMap.has(key)
            ? fieldNameMap.get(key)
            : key;

          const humanReadableFieldName = fieldName.replaceAll('_', ' ');

          this.errors[fieldName] = e[key]
            .map((message) => `The ${humanReadableFieldName} ${message}.`);
        });

        this.feedback = 'Please ensure everything is entered correctly.';

        throw e;
      }

      await this.myTribusEmbed
        .login(this.contact.login, this.contact.password);
    },

    /** @return {void} */
    clearErrors() {
      this.errors = {};
    },

    /** @return {Promise<void>} */
    async submit() {
      // @TODO: Validation of fields can be improved.
      // In that it doesn't validate everything at once, and instead does it in stages).

      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      if (!this.$store.state.auth.contact.exists()) {
        try {
          await this.createContactAndLogin();
        } catch (e) {
          this.isLoading = false;
          return;
        }
      }

      await this.sendMessage();
      this.isLoading = false;
    },

    /** @return {Promise<void>} */
    async sendMessage() {
      if (isEmptyString(this.message.body)) {
        this.errors.message = [
          'The message cannot be empty.',
        ];
        return;
      }

      this.clearErrors();

      this.message.property_id = this.property.id;
      this.message.user_id = this.listAgents.length > 0
        ? this.listAgents[0].id
        : null;

      try {
        await this.message.save();
      } catch (e) {
        this.feedback = 'Message Error';
        return;
      }

      this.feedback = 'An email confirmation will be sent to your account.';
      this.brytescoreRequestedInfo();
    },

    async brytescoreRequestedInfo() {
      const listing = getBrytescoreListing(this.property);

      await this.fetchAgent();

      const form = {
        fromEmail: this.contactEmail,
        fromName: this.contactName,
        fromTelephone: {
          mobile: this.contactTelephone,
        },
        message: this.message.body,
        name: this.formTitle,
        toEmail: this.agentEmail,
        toName: this.agentName,
        relatedListing: listing,
      };

      window.brytescore('realestate.requestedInfo', { form });
    },

    /** @return {void} */
    updatePhoneNumber(value) {
      const newValue = value.replace(/\D/g, '');

      if (newValue === '') {
        if (this.contact.phone_numbers.length > 0) {
          this.contact.phone_numbers = [];
        }
        return;
      }

      if (this.contact.phone_numbers.length === 0) {
        this.contact.phone_numbers.push({
          primary: true,
          type: 'Mobile',
        });
      }

      this.contact.phone_numbers[0].number = newValue;
    },
  },
};
</script>
